import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import ImageUploadField from "@/components/ImageUploadField";

const EMPTY = { title: "", subtitle: "", body: "", category: "Insight", author: "Carlton Elleker Chase", cover_image: "", published: true, department_ids: [], service_ids: [] };

export default function AdminInsights() {
  const { headers } = useAuth();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, d, s] = await Promise.all([
        api.get("/admin/insights", { headers }),
        api.get("/admin/departments", { headers }),
        api.get("/admin/services", { headers }),
      ]);
      setItems(a.data); setDepartments(d.data); setServices(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // body has its required attribute removed (the input was replaced by an editor)
      if (!editing.title || !editing.body) {
        setBusy(false);
        return toast.error("Title and body are required.");
      }
      if (editing.id) await api.put(`/admin/insights/${editing.id}`, editing, { headers });
      else await api.post(`/admin/insights`, editing, { headers });
      toast.success("Saved.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this insight?")) return;
    try { await api.delete(`/admin/insights/${id}`, { headers }); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleId = (key) => (id) => setEditing((cur) => {
    const arr = cur[key] || [];
    return { ...cur, [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
  });

  return (
    <div data-testid="admin-insights">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="overline text-graphite/55">Insights</p>
          <h1 className="mt-3 h-display text-4xl md:text-5xl">Manage articles <span className="text-graphite/40 text-xl">· {items.length}</span></h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="cec-btn cec-btn-primary" data-testid="admin-add-insight">
          <Plus size={16} /> New article
        </button>
      </div>

      <div className="hairline-top">
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-12 gap-4 py-5 hairline-bottom items-center" data-testid={`admin-insight-${it.id}`}>
            <div className="col-span-7">
              <p className="font-semibold">{it.title}</p>
              <p className="text-sm text-graphite/60 truncate">{it.subtitle}</p>
            </div>
            <div className="col-span-2 text-sm">{(it.department_ids || []).length}d / {(it.service_ids || []).length}s</div>
            <div className="col-span-1 text-xs">
              <span className={`px-2 py-1 ${it.published ? "bg-[#1E3668] text-warmwhite" : "hairline text-graphite/65"}`}>{it.published ? "Live" : "Draft"}</span>
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button className="p-2 hover:text-[#1E3668]" onClick={() => setEditing({ ...it })} aria-label="Edit" data-testid={`admin-edit-insight-${it.id}`}><Pencil size={16} /></button>
              <button className="p-2 hover:text-red-700" onClick={() => del(it.id)} aria-label="Delete" data-testid={`admin-delete-insight-${it.id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-graphite/40 flex items-start justify-center px-4 overflow-y-auto py-12" onClick={() => setEditing(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-warmwhite max-w-4xl w-full p-8 md:p-12 hairline" data-testid="admin-insight-form">
            <div className="flex items-start justify-between">
              <div>
                <p className="overline text-graphite/55">{editing.id ? "Edit" : "New"} insight</p>
                <h2 className="mt-3 h-display text-3xl">{editing.title || "Untitled"}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-2"><X size={18} /></button>
            </div>
            <div className="space-y-2 mt-8">
              <div><label className="overline text-graphite/55">Title</label><input className="edit-input" required value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><label className="overline text-graphite/55">Subtitle</label><input className="edit-input" value={editing.subtitle || ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-8">
                <div><label className="overline text-graphite/55">Category</label><input className="edit-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} /></div>
                <div><label className="overline text-graphite/55">Author</label><input className="edit-input" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
              </div>
              <div><label className="overline text-graphite/55">Cover image</label>
                <ImageUploadField
                  label=""
                  value={editing.cover_image}
                  onChange={(url) => setEditing({ ...editing, cover_image: url })}
                  testId="admin-insight-cover"
                />
              </div>
              <div className="mt-2">
                <label className="overline text-graphite/55 block mb-2">Body</label>
                <RichTextEditor
                  value={editing.body || ""}
                  onChange={(html) => setEditing({ ...editing, body: html })}
                  placeholder="Begin the article…"
                  minHeight={320}
                  testId="admin-insight-body"
                />
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <p className="overline text-graphite/55 mb-3">Departments ({(editing.department_ids || []).length})</p>
                <div className="max-h-56 overflow-y-auto hairline p-3 space-y-1">
                  {departments.map((d) => (
                    <label key={d.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-[#1E3668]">
                      <input type="checkbox" checked={(editing.department_ids || []).includes(d.id)} onChange={() => toggleId("department_ids")(d.id)} />
                      <span>{d.title}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <p className="overline text-graphite/55 mb-3">Services ({(editing.service_ids || []).length})</p>
                <div className="max-h-56 overflow-y-auto hairline p-3 space-y-1">
                  {services.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:text-[#1E3668]">
                      <input type="checkbox" checked={(editing.service_ids || []).includes(s.id)} onChange={() => toggleId("service_ids")(s.id)} />
                      <span>{s.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <label className="flex items-center gap-3 mt-6 text-sm">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="admin-insight-published" />
              Published
            </label>
            <div className="mt-10 flex items-center gap-3">
              <button type="submit" disabled={busy} className="cec-btn cec-btn-primary" data-testid="admin-insight-save">{busy ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setEditing(null)} className="cec-btn">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
